/**
 * What the compass encodes, as a column on the left edge. Every row is a
 * filter: tap it and the pivots it describes stay bright while the rest dim;
 * tap again, or another row, to change. Hidden on the wall.
 */
import React from 'react';
import styled from '@emotion/styled';
import { DIRECTION, LANE } from '../PivotCard';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Wrap = styled.div`
  display: flex; flex-direction: column; gap: 2px; font-size: 0.7rem; line-height: 1.25; color: #9ca3af;
  width: 190px; flex: none; padding: 6px 4px; align-self: flex-start;
  .row { display: flex; align-items: center; gap: 7px; padding: 4px 6px; border-radius: 6px; cursor: pointer; text-align: left;
    background: transparent; border: 1px solid transparent; color: inherit; font: inherit; width: 100%; }
  .row:hover { background: rgba(255,255,255,0.05); }
  .row[aria-pressed="true"] { background: rgba(124,58,237,0.16); border-color: rgba(167,139,250,0.55); color: #e8e8ef; }
  .row:focus-visible { outline: 1px solid #a78bfa; }
  .sw { width: 11px; height: 11px; border-radius: 50%; display: inline-block; flex: none; }
  .g { font-size: 9px; font-weight: 700; color: #0f0f16; background: #e8e8ef; border-radius: 999px; width: 14px; height: 14px; display: inline-grid; place-items: center; flex: none; }
  .txt { flex: 1; min-width: 0; }
  .how { padding: 6px 6px 0; color: #6b7280; font-size: 0.66rem; line-height: 1.3; border-top: 1px solid rgba(255,255,255,0.08); margin-top: 4px; }
`;

export default function CompassLegend({ scenarioColors = [], highlight = null, onHighlight }) {
  const { t } = useTranslation();
  const pick = (key) => onHighlight?.(highlight === key ? null : key);
  const Row = ({ k, children }) => (
    <button type="button" className="row" aria-pressed={highlight === k} onClick={() => pick(k)}>{children}</button>
  );
  return (
    <Wrap aria-label={t('pivotsV2.legend.label')}>
      <Row k="attractor"><i className="sw" style={{ background: DIRECTION.attractor.color }} /><span className="txt">{t('pivotsV2.legend.attractor')}</span></Row>
      <Row k="opportunity"><i className="sw" style={{ background: DIRECTION.opportunity.color }} /><span className="txt">{t('pivotsV2.legend.opportunity')}</span></Row>
      <Row k="contested"><i className="sw" style={{ background: `linear-gradient(90deg, ${DIRECTION.attractor.color} 50%, ${DIRECTION.opportunity.color} 50%)` }} /><span className="txt">{t('pivotsV2.legend.contested')}</span></Row>
      <Row k="aiOnly"><i className="sw" style={{ border: '2px dashed #9ca3af', width: 9, height: 9 }} /><span className="txt">{t('pivotsV2.legend.aiOnly')}</span></Row>
      <Row k="structural"><i className="sw" style={{ background: LANE.rakenteellinen.color }} /><span className="txt">{t('pivotsV2.legend.structural')}</span></Row>
      <Row k="loop"><i className="g">↻</i><span className="txt">{t('pivotsV2.legend.loop')}</span></Row>
      <Row k="choice"><i className="g">⑂</i><span className="txt">{t('pivotsV2.legend.choice')}</span></Row>
      <Row k="cross"><i className="g">⇄</i><span className="txt">{t('pivotsV2.legend.cross')}</span></Row>
      <Row k="blocker"><i className="g">⛔</i><span className="txt">{t('pivotsV2.legend.blocker')}</span></Row>
      <div className="row" style={{ cursor: 'default' }}>
        <i className="sw" style={{ background: `conic-gradient(${scenarioColors.map((c, i) => `${c} ${(i / scenarioColors.length) * 100}% ${((i + 1) / scenarioColors.length) * 100}%`).join(', ') || '#4b4f63 0 100%'})`, WebkitMask: 'radial-gradient(circle, transparent 50%, #000 52%)', mask: 'radial-gradient(circle, transparent 50%, #000 52%)' }} />
        <span className="txt">{t('pivotsV2.legend.ring')}</span>
      </div>
      <div className="how">{t('pivotsV2.legend.how')}</div>
    </Wrap>
  );
}
